import React, { useState, useEffect, useRef } from 'react';
import './Navbar.css';
import { useNavigate } from "react-router-dom";
import simpleSessionTimer from '../utils/simpleSessionTimer';
import { LogOut as sharedLogOut } from '../globalfunctions.js';
import { useIsMobile } from '../hooks/mobile';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import MobileChrome from './mobile/MobileChrome';
import CommandPalette from './CommandPalette';
import useThemeMode, { THEME_ICONS, THEME_LABELS } from '../hooks/useThemeMode';

const Navbar = () => {
  const [searchString, setSearchString] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { mode: themeMode, cycle: cycleTheme } = useThemeMode();
  const searchContainerRef = useRef(null);

  // Dropdown closes on outside click, same as any standard dropdown -
  // replaces the old full-screen dark overlay that blanked out the rest
  // of the page while searching.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Ctrl/Cmd+K (or "/" outside a field) opens the command palette from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName) || e.target.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      } else if (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const { searchResults, customerResults, isSearching, hasQuery, hasAnyResults } = useGlobalSearch(searchString);

  const LogOut = () => {
    // Was only clearing the session-timer bookkeeping, not the actual
    // access token - meant clicking Log Out didn't really log you out.
    simpleSessionTimer.clearSession();
    sharedLogOut();
  };

  const clearSearch = () => {
    setSearchString('');
  };

  // Phones get the app-style top bar + bottom tab bar instead (all hooks above
  // have already run, so switching between layouts is safe).
  if (isMobile) return <MobileChrome />;

  return (
    <div className="top-bar">
      <div className="logo">
        <img src="/logo192.png" alt="Logo" style={{ width: '30%', height: 'auto' }} />
      </div>
      <div className={`search-container ${isSearchExpanded ? 'search-expanded' : ''}`} ref={searchContainerRef}>
        <input
          type="text"
          placeholder="Search customers, domains, invoices, tickets…"
          value={searchString}
          onChange={(e) => setSearchString(e.target.value)}
          onFocus={() => setIsSearchExpanded(true)}
        />
        {!searchString && !isSearchExpanded && (
          <button type="button" className="search-hint" onClick={() => setPaletteOpen(true)} aria-label="Open the command palette">
            <kbd>Ctrl</kbd><kbd>K</kbd>
          </button>
        )}
        {hasQuery && isSearchExpanded && (
          <div className="search-results-panel">
              {isSearching && <div className="search-status">Searching...</div>}

              {!isSearching && !hasAnyResults && (
                <div className="search-empty">No customers, contacts, emails, or domains match "{searchString.trim()}".</div>
              )}

              {customerResults.length > 0 && (
                <div className="search-section">
                  <div className="search-section-label">Customers</div>
                  {customerResults.map((customer) => {
                    const displayName = customer.companyName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
                    const matchedDomain = customer.domains.find((d) => d.toLowerCase().includes(searchString.trim().toLowerCase()));
                    return (
                      <div
                        key={customer.id}
                        className="search-result-item customer-result"
                        onClick={() => {
                          navigate(`/customer/${customer.id}`);
                          clearSearch();
                        }}
                      >
                        <i className="fas fa-building customer-result-icon"></i>
                        <div className="customer-result-body">
                          <div className="customer-result-title">
                            <strong>{displayName || customer.email}</strong>
                            <span className={`status-badge ${customer.status && customer.status.toLowerCase() === 'active' ? 'status-active' : 'status-unknown'}`}>
                              {customer.status || 'Unknown'}
                            </span>
                            {customer.hasOverduePayment && (
                              <span className="status-badge status-overdue">Overdue Payment</span>
                            )}
                          </div>
                          <div className="customer-result-meta">
                            {customer.email}
                            {customer.domains.length > 0 && (
                              <span> &middot; {customer.domains.join(', ')}</span>
                            )}
                          </div>
                          {matchedDomain && <div className="customer-result-match">matched domain: {matchedDomain}</div>}
                          {customer.openTicketCount > 0 && (
                            <div className="customer-result-tickets">
                              <div className="customer-result-tickets-label">
                                {customer.openTicketCount} open ticket{customer.openTicketCount === 1 ? '' : 's'}
                              </div>
                              {customer.openTickets.map((ticket) => (
                                <button
                                  key={ticket.ticket_id}
                                  type="button"
                                  className="customer-result-ticket"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/tickets/${ticket.ticket_id}`);
                                    clearSearch();
                                  }}
                                >
                                  <span className="customer-result-ticket-number">#{ticket.ticket_number}</span>
                                  <span className="customer-result-ticket-subject">{ticket.subject}</span>
                                  <span className={`customer-result-ticket-status ticket-status-${ticket.status}`}>{ticket.status.replace('_', ' ')}</span>
                                </button>
                              ))}
                              {customer.openTicketCount > customer.openTickets.length && (
                                <div className="customer-result-tickets-more">+ {customer.openTicketCount - customer.openTickets.length} more</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {searchResults.tickets.length > 0 && (
                <div className="search-section">
                  <div className="search-section-label">Tickets</div>
                  {searchResults.tickets.map((ticketResult) => (
                    <div
                      key={ticketResult.ticket_id}
                      className="search-result-item"
                      onClick={() => {
                        navigate(`/tickets/${ticketResult.ticket_id}`);
                        clearSearch();
                      }}
                    >
                      <i className="fas fa-ticket" style={{ color: '#F0493E' }}></i>&nbsp;
                      <strong>#{ticketResult.ticket_number}</strong> {ticketResult.subject}
                    </div>
                  ))}
                </div>
              )}

              {searchResults.invoices.length > 0 && (
                <div className="search-section">
                  <div className="search-section-label">Invoices</div>
                  {searchResults.invoices.map((result) => (
                    <div
                      key={result.id}
                      className="search-result-item"
                      onClick={() => {
                        navigate(`/invoices/editinvoice/${result.invoice_number}`);
                        clearSearch();
                      }}
                    >
                      <i className="fas fa-file-invoice" style={{ color: '#007bff' }}></i>&nbsp;
                      <strong>{result.invoice_number}</strong>
                    </div>
                  ))}
                </div>
              )}

              {searchResults.users.length > 0 && (
                <div className="search-section">
                  <div className="search-section-label">Users</div>
                  {searchResults.users.map((userResult) => (
                    <div
                      key={userResult.id}
                      className="search-result-item"
                      onClick={() => {
                        navigate(`/customer/${userResult.id}`);
                        clearSearch();
                      }}
                    >
                      <i className="fas fa-user" style={{ color: '#00AA00' }}></i>&nbsp;
                      <strong>{userResult.first_name}</strong> ({userResult.email})
                    </div>
                  ))}
                </div>
              )}

              {searchResults.purchases.length > 0 && (
                <div className="search-section">
                  <div className="search-section-label">Purchases</div>
                  {searchResults.purchases.map((purchaseResult) => (
                    <div
                      key={purchaseResult.id}
                      className="search-result-item"
                      onClick={() => {
                        navigate(`/invoices/editinvoice/${purchaseResult.invoice_number}`);
                        clearSearch();
                      }}
                    >
                      <i className="fas fa-money-bill" style={{ color: '#CC6600' }}></i>&nbsp;
                      <strong>{purchaseResult.product_name} {purchaseResult.remarks}</strong>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} variant="palette" />
      <div className="user-settings">
        <i
          className={`fas ${THEME_ICONS[themeMode]}`}
          role="button"
          tabIndex={0}
          title={`Theme: ${THEME_LABELS[themeMode]} (click to change)`}
          aria-label={`Theme: ${THEME_LABELS[themeMode]}. Click to change.`}
          onClick={cycleTheme}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); cycleTheme(); } }}
        ></i>
        <i className="fas fa-cogs" onClick={() => navigate('/settings')}></i>
        <i onClick={LogOut} className="fas fa-sign-out-alt"></i>
      </div>
    </div>
  );
};

export default Navbar;
